import * as TasksApi from '@client-shared/api/tasks.api.js'
import filterFunctions from '@client-shared/utils/filter-functions'
import getTaskSortFunction from '@client-shared/utils/get-task-sort-function'
import ignoreCanceledRequest from '@client-shared/utils/ignore-canceled-request.js'
import { sortItemsByLocationLayerOrder } from '@client-shared/utils/sort-functions'

import constants from '@/config/constants.js'

const fetchAbortControllers = {}

export default {
  namespaced: true,

  state: () => {
    return {
      list: [],
      // Project id this store's `list` was last fetched for. Lets the socket
      // reconnect catch-up skip stores that have never been loaded for the
      // current project (see cliesrc/utils/refetch-project-data.js).
      loadedProjectId: null,
      markedTask: null,
      selectedMultiActionProperty: constants.MULTI_ACTION.TASK_ASSIGNEE,

      sortSettings: {
        sortProperty: constants.SORT_PROPERTIES.TASK_CONSECUTIVE_NUMBER,
        sortOrderReversed: false,
      },

      listStyle: constants.TASK_LIST_STYLES.LIST_VIEW, // Valid values: [constants.TASK_LIST_STYLES.LIST_VIEW, constants.TASK_LIST_STYLES.CARD_VIEW]

      printSettings: {
        groupBy: constants.TASKS_LIST_GROUPING.GROUP_BY_NONE,
        showTaskUrl: false,
        showTaskDescription: false,
        showTaskLog: false,
        showAllTaskImages: true,
        showOneTaskPerPage: true,
      },

      listSettings: {
        enableGroupBy: true,
      },

      // Only relevant for modus where tasks can be selected
      selection: {
        selectedTaskIds: [],
        shouldKeepSelected: true,
      },
    }
  },

  getters: {
    // Checked for every task of the list while selected tasks are kept: a Set instead of an
    // includes over the whole selection
    selectedTaskIdsSet: (state) => {
      return new Set(state.selection.selectedTaskIds)
    },

    listCountPublicUntrashed: (state, getters) => {
      return state.list
        .filter(task => task.state !== constants.VALID_TASK_STATES.DRAFT && !task.trashed)
        .length
    },

    getListMap: (state) => {
      return new Map(state.list.map(task => [
        task._id,
        task,
      ]))
    },

    getById: (state, getters) => {
      return taskId => getters.getListMap.get(taskId)
    },

    _listFilteredBySubTab: (state, getters, rootState) => {
      if (rootState.tasksListModus.subTab === constants.TASK_LIST_SUB_TABS.DRAFTS) {
        return getters.tasksDrafts
      }

      if (rootState.tasksListModus.subTab === constants.TASK_LIST_SUB_TABS.MAIN) {
        return getters.tasksMain
      }

      if (rootState.tasksListModus.subTab === constants.TASK_LIST_SUB_TABS.DONE) {
        return getters.tasksDone
      }

      if (rootState.tasksListModus.subTab === constants.TASK_LIST_SUB_TABS.TRASH) {
        return getters.tasksTrashed
      }

      return state.list
    },

    listFilteredByModus: (state, getters, rootState, rootGetters) => {
      const baseTasks = getters._listFilteredBySubTab
      const modus = rootState.tasksListModus.modus

      if (modus === constants.LIST_MODUS.MULTI_ACTION_EDIT) {
        return baseTasks.filter(task => {
          return !task.trashed && rootGetters['acl/isAuthorizedToUpdateTask'](task)
        })
      }

      return baseTasks
    },

    listFiltered: (state, getters, rootState, rootGetters) => {
      if (!rootGetters['tasksListModus/filterStoreName']) {
        return getters.listFilteredByModus
      }

      const modus = rootState.tasksListModus.modus

      return getters.listFilteredByModus.filter(task => {
        if (modus === constants.LIST_MODUS.MULTI_ACTION_EDIT && state.selection.shouldKeepSelected && getters.selectedTaskIdsSet.has(task._id)) {
          return true
        }

        return filterFunctions.tasks.matchesFilters({
          task,
          project: rootState.project.project,
          ...rootState[rootGetters['tasksListModus/filterStoreName']],
        })
      })
    },

    listFilteredSorted: (state, getters) => {
      const listFiltered = getters.listFiltered || []

      return [ ...listFiltered ].sort((a, b) => {
        return getters.groupFunction(a, b) || getters.sortFunction(a, b)
      })
    },

    tasksDrafts: (state, getters) => {
      return state.list.filter(task => task.state === constants.VALID_TASK_STATES.DRAFT)
    },

    tasksMain: (state, getters) => {
      return state.list.filter(task => task.state !== constants.VALID_TASK_STATES.DRAFT && task.state !== constants.VALID_TASK_STATES.DONE && !task.trashed)
    },

    tasksDone: (state, getters) => {
      return state.list.filter(task => task.state === constants.VALID_TASK_STATES.DONE && !task.trashed)
    },

    tasksTrashed: (state, getters) => {
      return state.list.filter(task => task.state !== constants.VALID_TASK_STATES.DRAFT && task.trashed)
    },

    canUseLocationLayerGrouping: (state, getters, rootState, rootGetters) => {
      const hasSelectableLayers = rootGetters['locationLayers/listSelectable'].length > 0
      const hasTasksWithLocationLayer = Boolean(state.list.find(task => task.locationLayerId))

      return hasSelectableLayers || hasTasksWithLocationLayer
    },

    usesLocationLayerGrouping: (state, getters, rootState, rootGetters) => {
      const tasksListModus = rootState.tasksListModus.modus
      const groupBy = state.printSettings.groupBy

      return (
        ((tasksListModus === constants.LIST_MODUS.PRINT || tasksListModus === constants.LIST_MODUS.EXPORT) && groupBy === constants.TASKS_LIST_GROUPING.GROUP_BY_LOCATION_LAYER) ||
        ((tasksListModus !== constants.LIST_MODUS.PRINT && tasksListModus !== constants.LIST_MODUS.EXPORT) && state.listSettings.enableGroupBy && getters.canUseLocationLayerGrouping)
      )
    },

    usesTaskTypeGrouping: (state, getters, rootState) => {
      return (rootState.tasksListModus.modus === constants.LIST_MODUS.PRINT || rootState.tasksListModus.modus === constants.LIST_MODUS.EXPORT) && state.printSettings.groupBy === constants.TASKS_LIST_GROUPING.GROUP_BY_TYPE
    },

    getHighestTaskNumber: (state) => {
      const taskConsecutiveNumbers = state.list
        .map(task => task.consecutiveNumber)
        .filter(consecutiveNumber => consecutiveNumber)

      return Math.max(0, ...taskConsecutiveNumbers)
    },

    groupFunction: (state, getters, rootState, rootGetters) => {
      if (getters.usesTaskTypeGrouping) {
        return (a, b) => {
          if (a.type === b.type) { return 0 }

          return a.type > b.type ? 1 : -1
        }
      }

      if (getters.usesLocationLayerGrouping) {
        return sortItemsByLocationLayerOrder.bind({ locationLayers: rootState.locationLayers.list })
      }

      return () => 0
    },

    sortFunction: (state, getters, rootState, rootGetters) => {
      return getTaskSortFunction({
        sortProperty: state.sortSettings.sortProperty,
        sortOrderReversed: state.sortSettings.sortOrderReversed,
        project: rootState.project.project,
        selectedTaskIds: state.selection.selectedTaskIds,
        getPfFileById: rootGetters['pfFiles/getById'],
      })
    },
  },

  mutations: {
    SET_TASKS (state, tasks) {
      state.list = tasks
    },

    SET_LOADED_PROJECT_ID (state, projectId) {
      state.loadedProjectId = projectId
    },

    REPLACE_TASK (state, updatedTask) {
      const existingTaskIndex = state.list.findIndex(task => task._id === updatedTask._id)

      if (existingTaskIndex !== -1) {
        const isUpdateNewer = new Date(updatedTask.modified) > new Date(state.list[existingTaskIndex].modified)

        if (isUpdateNewer) {
          state.list[existingTaskIndex] = updatedTask
        }
      }
    },

    ADD_TASK (state, taskToAdd) {
      const existingTaskIndex = state.list.findIndex(task => task._id === taskToAdd._id)

      if (existingTaskIndex !== -1) {
        const isUpdateNewer = new Date(taskToAdd.modified) > new Date(state.list[existingTaskIndex].modified)

        if (isUpdateNewer) {
          state.list[existingTaskIndex] = taskToAdd
        }
      } else {
        state.list.push(taskToAdd)
      }
    },

    REMOVE_TASK (state, taskId) {
      state.list = state.list.filter(task => task._id !== taskId)
    },

    RESET_TASKS (state) {
      state.list = []
      // Kept in sync with `list`: the tab-unmount wipe must also clear the
      // loadedProjectId gate (socket-io TRIGGER_RELOAD_* handlers +
      // refetch-project-data on reconnect both key off this flag), otherwise
      // they'd refetch into a list the user just navigated away from.
      state.loadedProjectId = null
    },

    SET_MARKED_TASK (state, task) {
      state.markedTask = task
    },

    RESET_SORTING (state) {
      state.sortSettings.sortProperty = constants.SORT_PROPERTIES.TASK_CONSECUTIVE_NUMBER
      state.sortSettings.sortOrderReversed = false
    },

    SET_SORT_PROPERTY (state, sortProperty) {
      state.sortSettings.sortProperty = sortProperty
    },

    SET_SORT_ORDER_REVERSED (state, sortOrderReversed) {
      state.sortSettings.sortOrderReversed = sortOrderReversed
    },

    SET_GROUP_BY (state, groupBy) {
      if (!Object.values(constants.TASKS_LIST_GROUPING)
        .includes(groupBy)) {
        console.error(`${groupBy} is not a valid grouping mode for task list`)

        return
      }

      state.printSettings.groupBy = groupBy
    },

    SET_SHOW_TASK_URL (state, newValue) {
      state.printSettings.showTaskUrl = newValue
    },

    SET_SHOW_TASK_DESCRIPTION (state, newValue) {
      state.printSettings.showTaskDescription = newValue
    },

    SET_SHOW_TASK_LOG (state, newValue) {
      state.printSettings.showTaskLog = newValue
    },

    SET_SHOW_ALL_TASK_IMAGES (state, newValue) {
      state.printSettings.showAllTaskImages = newValue
    },

    SET_SHOW_ONE_TASK_PER_PAGE (state, newValue) {
      state.printSettings.showOneTaskPerPage = newValue
    },

    SET_LIST_STYLE (state, newValue) {
      const validValues = [
        constants.TASK_LIST_STYLES.LIST_VIEW,
        constants.TASK_LIST_STYLES.CARD_VIEW,
      ]

      if (!validValues.includes(newValue)) {
        throw new Error(`Invalid list style: ${newValue}. Allowed values: ${validValues.join(', ')}`)
      }

      state.listStyle = newValue
    },

    TOGGLE_ENABLE_GROUP_BY (state) {
      state.listSettings.enableGroupBy = !state.listSettings.enableGroupBy
    },

    ADD_SELECTED_TASK_ID (state, taskId) {
      if (!state.selection.selectedTaskIds.includes(taskId)) {
        state.selection.selectedTaskIds.push(taskId)
      }
    },

    REMOVE_SELECTED_TASK_ID (state, taskId) {
      state.selection.selectedTaskIds = state.selection.selectedTaskIds.filter(id => id !== taskId)
    },

    SET_SHOULD_KEEP_SELECTED (state, shouldKeepSelected) {
      state.selection.shouldKeepSelected = shouldKeepSelected
    },

    RESET_SELECTION (state) {
      state.selection = {
        selectedTaskIds: [],
        shouldKeepSelected: true,
      }
    },

    SET_SELECTED_MULTI_ACTION_PROPERTY (state, multiActionProperty) {
      state.selectedMultiActionProperty = multiActionProperty
    },

    FULL_RESET (state) {
      state.list = []
      state.loadedProjectId = null
      state.markedTask = null
      state.selectedMultiActionProperty = constants.MULTI_ACTION.TASK_ASSIGNEE

      state.sortSettings = {
        sortProperty: constants.SORT_PROPERTIES.TASK_CONSECUTIVE_NUMBER,
        sortOrderReversed: false,
      }

      state.listStyle = constants.TASK_LIST_STYLES.LIST_VIEW

      state.printSettings = {
        groupBy: constants.TASKS_LIST_GROUPING.GROUP_BY_NONE,
        showTaskUrl: false,
        showTaskDescription: false,
        showTaskLog: false,
        showAllTaskImages: true,
        showOneTaskPerPage: true,
      }

      state.listSettings = {
        enableGroupBy: true,
      }

      state.selection = {
        selectedTaskIds: [],
        shouldKeepSelected: true,
      }
    },
  },

  actions: {
    async fetch ({ commit, rootState }, { projectId }) { // On the web client we load all tasks for now
      if (fetchAbortControllers.fetch instanceof AbortController) {
        fetchAbortControllers.fetch.abort()
      }

      fetchAbortControllers.fetch = new AbortController()

      const response = await ignoreCanceledRequest(TasksApi.getTasks({
        signal: fetchAbortControllers.fetch.signal,
        axios: this.$axios,
        projectId,
        loadAll: true,
      }))

      // The project was switched while the request was in flight: drop the previous project's list
      if (!response || rootState.project.requestedProjectId !== projectId) {
        return
      }

      commit('SET_TASKS', response.tasks)
      commit('SET_LOADED_PROJECT_ID', projectId)
    },

    async fetchById ({ commit, rootState }, taskId) {
      const projectId = rootState.project.requestedProjectId

      if (fetchAbortControllers.fetchById instanceof AbortController) {
        fetchAbortControllers.fetchById.abort()
      }

      fetchAbortControllers.fetchById = new AbortController()

      const task = await ignoreCanceledRequest(TasksApi.getTask({
        signal: fetchAbortControllers.fetchById.signal,
        axios: this.$axios,
        taskId,
      }))

      // The project was switched while the request was in flight: drop the previous project's item
      if (!task || rootState.project.requestedProjectId !== projectId) {
        return
      }

      commit('ADD_TASK', task)
    },

    async updateTaskTitle ({ commit }, { task, title }) {
      const updatedTask = await TasksApi.updateTaskTitle({
        axios: this.$axios,
        task,
        title,
      })

      commit('REPLACE_TASK', updatedTask)
    },

    async updateTaskDescription ({ commit }, { task, description }) {
      const updatedTask = await TasksApi.updateTaskDescription({
        axios: this.$axios,
        task,
        description,
      })

      commit('REPLACE_TASK', updatedTask)
    },

    async updateTaskType ({ commit }, { task, type }) {
      const updatedTask = await TasksApi.updateTaskType({
        axios: this.$axios,
        task,
        type,
      })

      commit('REPLACE_TASK', updatedTask)
    },

    async updateTaskAssigneeUserId ({ commit }, { task, userId }) {
      const updatedTask = await TasksApi.updateTaskAssigneeUserId({
        axios: this.$axios,
        task,
        userId,
      })

      commit('REPLACE_TASK', updatedTask)
    },

    async updateTaskDueDate ({ commit }, { task, dueDate }) {
      if (dueDate === task.dueDate) {
        return
      }

      const updatedTask = await TasksApi.updateTaskDueDate({
        axios: this.$axios,
        task,
        dueDate,
      })

      commit('REPLACE_TASK', updatedTask)
    },

    async publishTask ({ commit }, { task }) {
      const updatedTask = await TasksApi.publishTask({
        axios: this.$axios,
        task,
      })

      commit('REPLACE_TASK', updatedTask)
    },

    async updateTaskLocations ({ commit, rootGetters }, { task, location1, location2, location3, location4, locationLayerId, locationLayerVersionId, locationPins }) {
      const updatedTask = await TasksApi.updateTaskLocations({
        axios: this.$axios,
        task,
        location1,
        location2,
        location3,
        location4,
        locationLayerId,
        locationLayerVersionId,
        locationPins,
      })

      commit('REPLACE_TASK', updatedTask)
    },

    async updateTaskTags ({ commit, dispatch }, { item, newValue }) {
      const updatedTask = await TasksApi.updateTaskTags({
        axios: this.$axios,
        task: item,
        tags: newValue,
      })

      await dispatch('project/fetch', item.projectId, { root: true })

      commit('REPLACE_TASK', updatedTask)
    },

    async deleteTask ({ commit, rootGetters }, { task }) {
      if (task.state === constants.VALID_TASK_STATES.DRAFT) {
        await TasksApi.deleteTaskHard({
          axios: this.$axios,
          task,
        })

        commit('REMOVE_TASK', task._id)
      } else {
        const updatedTask = await TasksApi.updateTrashed({
          axios: this.$axios,
          task,
          isTrashed: true,
        })

        commit('REPLACE_TASK', updatedTask)
      }
    },

    async restoreTask ({ commit, rootGetters }, { task }) {
      const updatedTask = await TasksApi.updateTrashed({
        axios: this.$axios,
        task,
        isTrashed: false,
      })

      commit('REPLACE_TASK', updatedTask)
    },

    async updateTaskCommentMessage ({ commit }, { task, taskComment, message }) {
      const updatedTask = await TasksApi.updateTaskCommentMessage({
        axios: this.$axios,
        task,
        taskComment,
        message,
      })

      commit('REPLACE_TASK', updatedTask)
    },
  },
}
